import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './LoginScreen.css';
import config from '../config';
import VeritageLogo from '../components/VeritageLogo';
import WaveLines from '../components/WaveLines';

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export default function LoginScreen({ onLogin }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(null);

  useEffect(() => {
    const verified = searchParams.get('verified');
    const errorParam = searchParams.get('error');
    if (verified === '1') {
      setSuccess('Your account has been verified. You can now sign in.');
      setSearchParams({}, { replace: true });
    } else if (errorParam) {
      setError(decodeURIComponent(errorParam));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const canSubmit = useMemo(
    () => isValidEmail(email) && password.trim().length >= 6 && !isSubmitting,
    [email, password, isSubmitting]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!isValidEmail(email)) { setError('Enter a valid email address.'); return; }
    if (password.trim().length < 6) { setError('Password must be at least 6 characters.'); return; }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error ?? data?.message ?? 'Login failed.');
      if (data.jwt) localStorage.setItem('vt_dashboard_token', data.jwt);
      onLogin(data);
    } catch (err) {
      setError(err?.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vt-login">
      <WaveLines className="vt-loginWaves" lines={30} amplitude={110} opacity={0.6} />
      <div className="vt-loginShell">
        <div className="vt-loginBrand" aria-label="Veritage">
          <VeritageLogo className="vt-loginLogo" height={28} />
          <div className="vt-loginBrandText">
            <div className="vt-loginBrandTag">Dashboard</div>
          </div>
        </div>

        <div className="vt-loginCard vt-surface">
          <div className="vt-loginHeader">
            <h1 className="vt-loginTitle">Sign in</h1>
            <p className="vt-loginSubtitle vt-muted">Use your organisation credentials to access the dashboard.</p>
          </div>

          <form className="vt-loginForm" onSubmit={handleSubmit}>
            <label className="vt-field">
              <span className="vt-fieldLabel">Email</span>
              <input className="vt-input" type="email" autoComplete="email" inputMode="email"
                placeholder="name@company.com" value={email} onChange={e => setEmail(e.target.value)} />
            </label>

            <label className="vt-field">
              <span className="vt-fieldLabel">Password</span>
              <input className="vt-input" type="password" autoComplete="current-password"
                placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
            </label>

            {error   && <div className="vt-loginError" role="alert">{error}</div>}
            {success && <div role="status" style={{ color: 'var(--vt-success)', marginBottom: '1rem', fontSize: 14 }}>{success}</div>}

            <button className="vt-btn vt-btn--primary vt-loginSubmit" type="submit" disabled={!canSubmit}>
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </button>

            <div className="vt-loginFooter">
              <span style={{ fontSize: 13, color: 'var(--vt-muted)' }}>
                Contact <a href="mailto:team@veritage.io" style={{ color: 'var(--vt-purple)', textDecoration: 'none' }}>team@veritage.io</a> to request access.
              </span>
            </div>
          </form>
        </div>

        <div className="vt-loginMeta vt-muted">
          <span>© {new Date().getFullYear()} Veritage</span>
          <span className="vt-loginDot" aria-hidden="true">•</span>
          <span>Security &amp; compliance by design</span>
        </div>
      </div>
    </div>
  );
}
